/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PageLayout from '../components/PageLayout';
import { listInstruments, getAnalysisJobStatus, listAnalysisJobs, deleteAnalysisJob } from '../api/api';
import { InstrumentDataStatusDisplay, AnalysisJobStatusDisplay } from '../utils/analysisConstants';
import AnalysisJobForm from '../components/analysis/AnalysisJobForm';
import AddInstrumentForm from '../components/analysis/AddInstrumentForm'; // Import AddInstrumentForm
import { Link, useLocation } from 'react-router-dom';
import { formatStoredDateTime } from '../utils/datetime';

const POLLING_INTERVAL = 5000; // 5 seconds
// Interval for refreshing instruments list after adding one, to see PENDING_INITIAL_DOWNLOAD status update
const INSTRUMENT_REFRESH_INTERVAL = 10000; // 10 seconds 
// No longer using LOCAL_STORAGE_SUBMITTED_JOBS_KEY

const prettifyAnalysisType = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return 'Generic Spec';
  }

  return value
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

const getJobStatusClasses = (status) => {
  if (status === 'COMPLETED_SUCCESS') {
    return 'bg-green-100 text-green-700';
  }
  if (status === 'COMPLETED_FAIL') {
    return 'bg-red-100 text-red-700';
  }
  if (status === 'RUNNING_ANALYSIS') {
    return 'bg-blue-100 text-blue-700';
  }
  if (status === 'UNKNOWN') {
    return 'bg-gray-100 text-gray-700';
  }
  return 'bg-yellow-100 text-yellow-700';
};

const getInstrumentStatusClasses = (status) => {
  if (status === 'AVAILABLE') {
    return 'bg-green-100 text-green-700';
  }
  if (status === 'PENDING_INITIAL_DOWNLOAD') {
    return 'bg-amber-100 text-amber-700';
  }
  if (status === 'ERROR') {
    return 'bg-red-100 text-red-700';
  }
  return 'bg-slate-100 text-slate-700';
};

const HistoricalAnalysisPage = () => {
  const location = useLocation();
  const [instruments, setInstruments] = useState([]);
  const [isLoadingInstruments, setIsLoadingInstruments] = useState(true);
  const [errorInstruments, setErrorInstruments] = useState(null);
  const [submittedJobs, setSubmittedJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true); // Now for loading jobs from API
  const [pageError, setPageError] = useState(null);
  const [instrumentMessage, setInstrumentMessage] = useState(null); // For messages related to adding instruments
  const [activeView, setActiveView] = useState('build');

  const fetchInstruments = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoadingInstruments(true);
      const data = await listInstruments();
      setInstruments(data);
      setErrorInstruments(null);
    } catch (error) {
      console.error("Failed to fetch instruments:", error);
      setErrorInstruments(error.toString() || "Failed to load instruments.");
      setInstruments([]);
    } finally {
      setIsLoadingInstruments(false);
    }
  }, []); // Added closing parenthesis and bracket for useCallback

  const fetchSubmittedJobs = useCallback(async () => {
    setIsLoadingJobs(true);
    try {
      const jobsData = await listAnalysisJobs();
      // API already orders by creation date, most recent first.
      setSubmittedJobs(jobsData);
      setPageError(null); // Clear previous errors
    } catch (error) {
      console.error("Failed to fetch submitted jobs:", error);
      setPageError(error.detail || error.message || "Failed to load submitted jobs.");
      setSubmittedJobs([]); // Clear jobs on error
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    fetchInstruments();
    fetchSubmittedJobs();
  }, [fetchInstruments, fetchSubmittedJobs]);

  const handleInstrumentAdded = (newlyAddedInstrument) => {
    // newInstrument might contain the full object or just a success indicator
    // We should refresh the list to get the latest statuses from the backend
    setInstrumentMessage(`Instrument ${newlyAddedInstrument.symbol || 'new instrument'} submitted. Refreshing list...`);
    // Optionally, add to list immediately with PENDING status if API doesn't return it fast enough
    // Or just rely on fetchInstruments to update.
    setTimeout(() => {
      fetchInstruments(false); // Fetch without full page loading indicator
      setInstrumentMessage(null); // Clear message after a bit
    }, 2000); // Give a moment for backend to process before refetch

    // More robust: poll fetchInstruments a few times if status is PENDING_INITIAL_DOWNLOAD
    let refreshCount = 0;
    const refreshInterval = setInterval(() => {
      if (refreshCount < 5) { // Poll 5 times (e.g. over 50 seconds)
        fetchInstruments(false);
        refreshCount++;
      } else {
        clearInterval(refreshInterval);
      }
    }, INSTRUMENT_REFRESH_INTERVAL);
  };

  const handleInstrumentAddError = useCallback((errorMessage) => {
    setPageError(`Failed to add instrument: ${errorMessage}`);
    setInstrumentMessage(null);
  }, []);

  const handleJobSubmitted = async (newJobInfo) => {
    // newJobInfo is the response from POST /api/analysis/analyze/
    // After successful submission, refresh the list of jobs from the server
    // to get the most up-to-date list including the new job.
    setPageError(null); // Clear previous form errors
    // Optionally, provide immediate feedback or add to list optimistically
    // For simplicity and consistency, we'll just refetch.
    try {
      // The submitAnalysisJob is already called by AnalysisJobForm.
      // Here, we just need to react to its success.
      // The onJobSubmitted callback in AnalysisJobForm now receives (response, originalFormData)
      // So newJobInfo here is the API response.
      await fetchSubmittedJobs(); // Refetch all jobs
      setActiveView('runs');
    } catch (error) {
      // This catch is more for if fetchSubmittedJobs fails after a successful submission
      console.error("Error refreshing job list after submission:", error);
      setPageError("Job submitted, but failed to refresh job list.");
    }
  };

  const handleFormError = useCallback((errorMessage) => { // This is for AnalysisJobForm errors
    setPageError(errorMessage);
  }, []);

  const handleDeleteJob = async (jobId) => {
    try {
      await deleteAnalysisJob(jobId);
      fetchSubmittedJobs();
    } catch (error) {
      setPageError(`Failed to delete job ${jobId}: ${error.message}`);
    }
  };

  useEffect(() => {
    const pollJobStatuses = async () => {
      if (submittedJobs.length === 0) return;

      const updatedJobsPromises = submittedJobs.map(async (job) => {
        if (job.status === "COMPLETED_SUCCESS" || job.status === "COMPLETED_FAIL") {
          return job; 
        }
        try {
          const updatedStatus = await getAnalysisJobStatus(job.job_id);
          return { ...job, ...updatedStatus }; 
        } catch (error) {
          console.error(`Failed to get status for job ${job.job_id}:`, error);
          return { ...job, error_polling: "Failed to update status" }; 
        }
      });
      
      const resolvedJobs = await Promise.all(updatedJobsPromises);
      setSubmittedJobs(resolvedJobs);
    };

    const intervalId = setInterval(pollJobStatuses, POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [submittedJobs]);

  const readyInstrumentCount = instruments.filter((instrument) => instrument.data_status === 'AVAILABLE').length;
  const runningJobCount = submittedJobs.filter(
    (job) => job.status !== 'COMPLETED_SUCCESS' && job.status !== 'COMPLETED_FAIL'
  ).length;
  const pendingInstrumentCount = instruments.filter((instrument) => instrument.data_status !== 'AVAILABLE').length;

  const pageTabs = [
    { key: 'build', label: 'Build', count: readyInstrumentCount },
    { key: 'runs', label: 'Runs', count: submittedJobs.length },
    { key: 'instruments', label: 'Instruments', count: instruments.length },
  ];

  return (
    <PageLayout
      title="Historical Data Analysis"
      backButton={{ label: 'Back to Research', to: '/research', preferHistory: false, hideLabelOnMobile: true }}
    >
      {pageError && <p className="mb-4 rounded-xl bg-red-100 p-3 text-red-500">{pageError}</p>}
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-textPrimary lg:text-3xl">Build and run historical studies</h2>
            <p className="mt-2 text-sm text-textSecondary">
              Keep the builder focused. Switch to runs or instruments only when you need them.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {pageTabs.map((tab) => {
              const isActive = activeView === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveView(tab.key)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                    isActive
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-backgroundSurface text-textPrimary hover:bg-backgroundBody border border-borderDefault'
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${isActive ? 'bg-white/20 text-white' : 'bg-backgroundBody text-textSecondary'}`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {activeView === 'build' && (
          <section className="rounded-3xl border border-borderDefault bg-backgroundSurface p-5 shadow-sm lg:p-6">
            <div className="mb-5 flex flex-wrap items-center gap-2 text-xs font-medium text-textSecondary">
              <span className="rounded-full bg-backgroundBody px-3 py-1.5">{readyInstrumentCount} ready instruments</span>
              <span className="rounded-full bg-backgroundBody px-3 py-1.5">{runningJobCount} active runs</span>
            </div>
            <AnalysisJobForm
              instruments={instruments}
              onJobSubmitted={handleJobSubmitted}
              onError={handleFormError}
              recoveryDraft={location.state?.analysisRecoveryDraft || null}
            />
          </section>
        )}

        {activeView === 'runs' && (
          <section className="rounded-3xl border border-borderDefault bg-backgroundSurface p-5 shadow-sm lg:p-6">
            <div className="mb-5 flex flex-wrap items-center gap-2 text-xs font-medium text-textSecondary">
              <span className="rounded-full bg-backgroundBody px-3 py-1.5">{submittedJobs.length} total</span>
              <span className="rounded-full bg-backgroundBody px-3 py-1.5">{runningJobCount} active</span>
            </div>
            {isLoadingJobs && <p className="text-textSecondary">Loading submitted jobs...</p>}
            {!isLoadingJobs && submittedJobs.length === 0 && (
              <div className="rounded-2xl border border-dashed border-borderDefault bg-backgroundBody p-6 text-sm text-textSecondary">
                No analysis jobs submitted yet.
              </div>
            )}
            {!isLoadingJobs && submittedJobs.length > 0 && (
              <ul className="space-y-3 max-h-[780px] overflow-y-auto pr-1">
                {submittedJobs.map((job) => (
                  <li key={job.job_id} className="rounded-2xl border border-borderDefault bg-backgroundBody p-4 shadow-sm">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link to={`/analysis/${job.job_id}`} className="break-all text-base font-semibold text-primary hover:underline">
                            {job.name || `Job ID: ${job.job_id}`}
                          </Link>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getJobStatusClasses(job.status)}`}>
                            {AnalysisJobStatusDisplay[job.status] || job.status}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium text-textSecondary">
                          <span className="rounded-full bg-backgroundSurface px-3 py-1.5">{prettifyAnalysisType(job.analysis_type)}</span>
                          <span className="rounded-full bg-backgroundSurface px-3 py-1.5">{job.instrument_symbol}</span>
                          <span className="rounded-full bg-backgroundSurface px-3 py-1.5">{job.target_timeframe}</span>
                        </div>
                        <div className="mt-3 text-xs text-textSecondary">
                          Submitted {formatStoredDateTime(job.created_at, undefined, 'N/A')}
                          {' · '}
                          Updated {formatStoredDateTime(job.updated_at, undefined, 'N/A')}
                        </div>
                        {job.error_polling && <p className="mt-2 text-xs text-red-500">{job.error_polling}</p>}
                        {job.status === 'UNKNOWN' && job.error_message && <p className="mt-2 text-xs text-red-500">{job.error_message}</p>}
                      </div>

                      {(job.status === 'COMPLETED_SUCCESS' || job.status === 'COMPLETED_FAIL') && (
                        <button
                          onClick={() => handleDeleteJob(job.job_id)}
                          className="inline-flex items-center justify-center rounded-xl bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-700"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {activeView === 'instruments' && (
          <section className="rounded-3xl border border-borderDefault bg-backgroundSurface p-5 shadow-sm lg:p-6">
            <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
              <div className="rounded-2xl border border-borderDefault bg-backgroundBody p-4">
                <h3 className="text-base font-semibold text-textPrimary">Add instrument</h3>
                <div className="mt-4">
                  <AddInstrumentForm 
                    onInstrumentAdded={handleInstrumentAdded}
                    onError={handleInstrumentAddError}
                  />
                </div>
                {instrumentMessage && <p className="mt-3 text-sm text-green-600">{instrumentMessage}</p>}
              </div>

              <div>
                <div className="mb-5 flex flex-wrap items-center gap-2 text-xs font-medium text-textSecondary">
                  <span className="rounded-full bg-green-100 px-3 py-1 text-green-700">{readyInstrumentCount} ready</span>
                  <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700">{pendingInstrumentCount} pending</span>
                </div>
                {isLoadingInstruments && <p className="text-textSecondary">Loading instruments...</p>}
                {errorInstruments && <p className="text-red-500">Error: {errorInstruments}</p>}
                {!isLoadingInstruments && !errorInstruments && instruments.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-borderDefault bg-backgroundBody p-5 text-sm text-textSecondary">
                    No instruments available yet.
                  </div>
                )}
                {!isLoadingInstruments && !errorInstruments && instruments.length > 0 && (
                  <ul className="space-y-3 max-h-[720px] overflow-y-auto pr-1">
                    {instruments.map((instrument) => (
                      <li key={instrument.symbol} className="rounded-2xl border border-borderDefault bg-backgroundBody p-4 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-medium text-textPrimary">{instrument.symbol} ({instrument.exchange})</div>
                            <div className="mt-1 text-sm text-textSecondary">
                              {InstrumentDataStatusDisplay[instrument.data_status] || instrument.data_status}
                            </div>
                          </div>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getInstrumentStatusClasses(instrument.data_status)}`}>
                            {instrument.data_status === 'AVAILABLE' ? 'Ready' : 'Syncing'}
                          </span>
                        </div>
                        <div className="mt-3 text-xs text-textSecondary">
                          Base {instrument.base_timeframe}
                          {instrument.last_updated ? ` · Updated ${formatStoredDateTime(instrument.last_updated, undefined, '-')}` : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    </PageLayout>
  );
};

export default HistoricalAnalysisPage;
